import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import './ClientRevenueAnalytics.css';

const ClientRevenueAnalytics = ({ clients, projects, chartColors }) => {
  const [selectedYear, setSelectedYear] = useState('all');
  const [topN, setTopN] = useState(25);
  const [showComparison, setShowComparison] = useState(false);

  const currentYear = new Date().getFullYear();

  // Get available years from project data
  const availableYears = useMemo(() => {
    const years = new Set();
    projects.forEach(project => {
      if (project.initialAcceptance) {
        years.add(new Date(project.initialAcceptance).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [projects]);

  // Filter clients for 3D Models only
  const modelClients = useMemo(() => {
    return clients.filter(client => {
      const services = client.services_provided || [];
      return services.some(s =>
        s === '3D Models' ||
        s.includes('3D Models')
      );
    });
  }, [clients]);

  // Get model client IDs for filtering projects
  const modelClientIds = useMemo(() => {
    return new Set(modelClients.map(c => c.id));
  }, [modelClients]);

  // Calculate revenue per client
  const clientRevenueData = useMemo(() => {
    const revenueByClient = {};

    projects.forEach(project => {
      if (!modelClientIds.has(project.clientId)) return;
      if (!project.initialAcceptance) return;

      const year = new Date(project.initialAcceptance).getFullYear();
      const price = project.tnRevenue ?? project.modelPrice ?? 0;

      if (!revenueByClient[project.clientId]) {
        // Find client name
        const client = modelClients.find(c => c.id === project.clientId);
        revenueByClient[project.clientId] = {
          clientId: project.clientId,
          clientName: client?.name || project.clientName || 'Unknown',
          totalAllTime: 0,
          byYear: {},
          projectCount: 0
        };
      }

      revenueByClient[project.clientId].totalAllTime += price;
      revenueByClient[project.clientId].projectCount += 1;

      if (!revenueByClient[project.clientId].byYear[year]) {
        revenueByClient[project.clientId].byYear[year] = 0;
      }
      revenueByClient[project.clientId].byYear[year] += price;
    });

    return Object.values(revenueByClient);
  }, [projects, modelClientIds, modelClients]);

  // Top clients for selected year
  const topClientsData = useMemo(() => {
    let data = clientRevenueData.map(client => {
      let revenue;
      if (selectedYear === 'all') {
        revenue = client.totalAllTime;
      } else {
        revenue = client.byYear[selectedYear] || 0;
      }
      return {
        ...client,
        revenue
      };
    });

    // Filter out clients with no revenue for selected period
    data = data.filter(c => c.revenue > 0);

    // Sort by revenue and take top N
    data.sort((a, b) => b.revenue - a.revenue);
    return data.slice(0, topN);
  }, [clientRevenueData, selectedYear, topN]);

  // Year-over-year comparison data
  const comparisonData = useMemo(() => {
    const compareYear = currentYear;
    const previousYear = currentYear - 1;

    let data = clientRevenueData.map(client => ({
      clientName: client.clientName,
      currentYear: client.byYear[compareYear] || 0,
      previousYear: client.byYear[previousYear] || 0,
      change: ((client.byYear[compareYear] || 0) - (client.byYear[previousYear] || 0))
    }));

    // Filter for clients with data in either year
    data = data.filter(c => c.currentYear > 0 || c.previousYear > 0);

    // Sort by current year revenue
    data.sort((a, b) => b.currentYear - a.currentYear);
    return data.slice(0, topN);
  }, [clientRevenueData, currentYear, topN]);

  // Yearly revenue trend
  const yearlyRevenueData = useMemo(() => {
    const yearlyTotals = {};

    projects.forEach(project => {
      if (!modelClientIds.has(project.clientId)) return;
      if (!project.initialAcceptance) return;

      const year = new Date(project.initialAcceptance).getFullYear();
      const price = project.tnRevenue ?? project.modelPrice ?? 0;

      if (!yearlyTotals[year]) {
        yearlyTotals[year] = {
          year,
          revenue: 0,
          projectCount: 0
        };
      }

      yearlyTotals[year].revenue += price;
      yearlyTotals[year].projectCount += 1;
    });

    return Object.values(yearlyTotals).sort((a, b) => a.year - b.year);
  }, [projects, modelClientIds]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Format short currency for axis
  const formatShortCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
    return `$${value}`;
  };

  // Custom tooltip for top clients
  const TopClientsTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="revenue-tooltip">
          <p className="tooltip-name">{data.clientName}</p>
          <p className="tooltip-value">{formatCurrency(data.revenue)}</p>
          <p className="tooltip-detail">{data.projectCount} projects</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for comparison
  const ComparisonTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const changePercent = data.previousYear > 0
        ? ((data.change / data.previousYear) * 100).toFixed(1)
        : data.currentYear > 0 ? '+100' : '0';

      return (
        <div className="revenue-tooltip">
          <p className="tooltip-name">{label}</p>
          <p className="tooltip-value" style={{ color: '#34d399' }}>
            {currentYear}: {formatCurrency(data.currentYear)}
          </p>
          <p className="tooltip-value" style={{ color: '#9CA3AF' }}>
            {currentYear - 1}: {formatCurrency(data.previousYear)}
          </p>
          <p className={`tooltip-change ${data.change >= 0 ? 'positive' : 'negative'}`}>
            {data.change >= 0 ? '+' : ''}{formatCurrency(data.change)} ({changePercent}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for yearly trend
  const YearlyTrendTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="revenue-tooltip">
          <p className="tooltip-name">{label}</p>
          <p className="tooltip-value">{formatCurrency(data.revenue)}</p>
          <p className="tooltip-detail">{data.projectCount} projects</p>
        </div>
      );
    }
    return null;
  };

  // Calculate totals for summary
  const totals = useMemo(() => {
    let totalRevenue = 0;
    let totalProjects = 0;

    if (selectedYear === 'all') {
      clientRevenueData.forEach(c => {
        totalRevenue += c.totalAllTime;
        totalProjects += c.projectCount;
      });
    } else {
      clientRevenueData.forEach(c => {
        totalRevenue += c.byYear[selectedYear] || 0;
        // Count projects for selected year
        projects.forEach(p => {
          if (p.clientId === c.clientId && p.initialAcceptance) {
            if (new Date(p.initialAcceptance).getFullYear() === selectedYear) {
              totalProjects += 1;
            }
          }
        });
      });
    }

    return { totalRevenue, totalProjects };
  }, [clientRevenueData, selectedYear, projects]);

  return (
    <div className="client-revenue-analytics">
      {/* Controls Row */}
      <div className="controls-row">
        <div className="controls-left">
          <div className="year-selector">
            <button
              className={`year-button ${selectedYear === 'all' ? 'active' : ''}`}
              onClick={() => setSelectedYear('all')}
            >
              All Time
            </button>
            {availableYears.map(year => (
              <button
                key={year}
                className={`year-button ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          <div className="top-n-selector">
            <label>Show Top:</label>
            <select
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              className="select-input"
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
            </select>
          </div>

          <button
            className={`comparison-toggle ${showComparison ? 'active' : ''}`}
            onClick={() => setShowComparison(!showComparison)}
          >
            Year-over-Year Comparison
          </button>
        </div>

        <div className="stats-summary">
          <span className="stat-item">
            <strong>{formatCurrency(totals.totalRevenue)}</strong> Total Revenue
          </span>
          <span className="stat-item">
            <strong>{totals.totalProjects}</strong> Projects
          </span>
        </div>
      </div>

      {/* Main Chart - Top Clients or Comparison */}
      {!showComparison ? (
        <div className="chart-card">
          <h3 className="chart-title">
            Top {topN} Clients by Revenue
            {selectedYear !== 'all' && ` (${selectedYear})`}
          </h3>
          <div className="horizontal-bar-chart-container">
            <ResponsiveContainer width="100%" height={Math.max(400, topClientsData.length * 35)}>
              <BarChart
                data={topClientsData}
                layout="vertical"
                margin={{ top: 20, right: 80, left: 150, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors?.borderPrimary || '#4B5563'} />
                <XAxis
                  type="number"
                  stroke={chartColors?.borderPrimary || '#4B5563'}
                  tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
                  tickFormatter={formatShortCurrency}
                />
                <YAxis
                  type="category"
                  dataKey="clientName"
                  stroke={chartColors?.borderPrimary || '#4B5563'}
                  tick={{ fill: chartColors?.textPrimary || '#F9FAFB', fontSize: 12 }}
                  width={140}
                />
                <Tooltip content={<TopClientsTooltip />} />
                <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                  {topClientsData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`hsl(${160 + index * 2}, 70%, ${55 - index * 1.5}%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="chart-card">
          <h3 className="chart-title">
            Year-over-Year Comparison ({currentYear} vs {currentYear - 1})
          </h3>
          <div className="comparison-chart-container">
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={comparisonData}
                layout="vertical"
                margin={{ top: 20, right: 80, left: 150, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors?.borderPrimary || '#4B5563'} />
                <XAxis
                  type="number"
                  stroke={chartColors?.borderPrimary || '#4B5563'}
                  tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
                  tickFormatter={formatShortCurrency}
                />
                <YAxis
                  type="category"
                  dataKey="clientName"
                  stroke={chartColors?.borderPrimary || '#4B5563'}
                  tick={{ fill: chartColors?.textPrimary || '#F9FAFB', fontSize: 12 }}
                  width={140}
                />
                <Tooltip content={<ComparisonTooltip />} />
                <Legend
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="rect"
                />
                <Bar dataKey="currentYear" name={`${currentYear}`} fill="#34d399" radius={[0, 4, 4, 0]} />
                <Bar dataKey="previousYear" name={`${currentYear - 1}`} fill="#9CA3AF" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Yearly Revenue Trend */}
      <div className="chart-card">
        <h3 className="chart-title">Revenue Trend by Year</h3>
        <div className="trend-chart-container">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={yearlyRevenueData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors?.borderPrimary || '#4B5563'} />
              <XAxis
                dataKey="year"
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
              />
              <YAxis
                stroke={chartColors?.borderPrimary || '#4B5563'}
                tick={{ fill: chartColors?.textPrimary || '#F9FAFB' }}
                tickFormatter={formatShortCurrency}
              />
              <Tooltip content={<YearlyTrendTooltip />} />
              <Bar dataKey="revenue" fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ClientRevenueAnalytics;
